import {observable} from 'mobx';
import {HostPageMessenger} from '../lib/HostPageMessenger';
import {UserFeatures} from '../types/UserFeatures';
import {PrismContext} from '../types/PrismContext';
import {AccountType} from '../types/AppConstants';

export class DirectoryStore {

  private static nonPermittedAppsForLiteAccount = ['file_export', 'file_upload_guide', 'rest_api', 'custom_endpoints'];

  @observable public trackerId?: string;
  @observable public userFeatures?: UserFeatures;
  @observable public prismContext?: PrismContext;
  @observable public permittedApps?: string[];

  // Published by AppDetailStore once it resolves the version, so router-level analytics
  // in Directory.tsx can report appVersion.
  @observable public activeAppId?: string;
  @observable public activeAppVersion?: string;
  @observable public activeAppInstalled?: boolean;

  constructor() {
    if (process.env.REACT_APP_TRACKER_ID) {
      this.trackerId = process.env.REACT_APP_TRACKER_ID;
    }
    HostPageMessenger.on('setTracker', (tracker) => {
      this.trackerId = tracker;
    });

    HostPageMessenger.on('ready', () => HostPageMessenger.send('getTracker'));
    HostPageMessenger.on('userFeatures', (data: UserFeatures) =>  {
      this.userFeatures = data;
      this.permittedApps =
        data?.variants?.setting_permitted_ocp_apps
          ? data?.variants.setting_permitted_ocp_apps.split(',')
          : [];
    });
    HostPageMessenger.on('prismContext', (prismContext: PrismContext) => {
      this.prismContext = prismContext;
    });
    HostPageMessenger.on('optiIdTokenUpdated', (message: {'token': string}) => {
      if (this.prismContext && this.prismContext.originalUser) {
        this.prismContext.originalUser.optiIdAccessToken = message.token;
      }
    });
  }

  public hasFeatureFlag = (flag: string) => {
    return this.userFeatures?.features?.[flag] || false;
  }

  public hasZaiusOnlyAccess = () => {
    return this.hasFeatureFlag('feature_zaius_only_access');
  }

  public requiresAccountUpgrade = (appId: string) => {
    if (this.permittedApps && this.permittedApps.length > 0 && !this.permittedApps.includes(appId)) {
      return true;
    } else if (this.showOcpUpgradeLock() && DirectoryStore.nonPermittedAppsForLiteAccount.includes(appId)) {
      return true;
    }

    return false;
  }

  public getAccountType = () => {
    return this.prismContext?.account.isOptiHub ? AccountType.HUB : AccountType.ODP;
  }

  public isODPAccount = () => {
    return this.getAccountType() === AccountType.ODP;
  }

  public isPassThroughAccount = () => {
    return this.hasFeatureFlag('feature_pass_through_ocp_apps');
  }

  public isPassThroughMinAccount = () => {
    return this.hasFeatureFlag('feature_pass_through_min_ocp_apps');
  }

  public isOAPassThroughMinAccount = () => {
    return this.hasFeatureFlag('feature_oa_pass_through_min_ocp_apps');
  }

  public showOcpUpgradeLock = () => {
    return this.hasFeatureFlag('feature_ocp_show_upgrade_lock');
  }

  public accountUpgradeMessage = () => {
    if (this.showOcpUpgradeLock()) {
      return 'Available with OCP upgrade';
    }
    return `Available with ${this.getAccountType()} upgrade`;
  }

  public getOptiAccessToken = (): string => {
    const token = this.prismContext?.originalUser.optiIdAccessToken || process.env.REACT_APP_PRISM_OPTIID_ACCESS_TOKEN;
    if (!token) {
      throw new Error('OptiID access token is missing.');
    }
    return token;
  }

  public getUserEmail = (): string => {
    const email = this.prismContext?.originalUser.email || process.env.REACT_APP_PRISM_USER_EMAIL;
    if (!email) {
      throw new Error('User email is missing.');
    }
    return email;
  }

  public getOrganizationId = (): string => {
    const organizationId = this.prismContext?.organization?.turnstileOrganizationId
     || process.env.REACT_APP_PRISM_ORGANIZATION_ID;
    if (!organizationId) {
      throw new Error('Organization ID is missing.');
    }
    return organizationId;
  }
}

export const directoryStore = new DirectoryStore();
